/*
 * Automatic page translation for the León Gallery site.
 *
 * Uses the Google Website Translator script, which translates the text that is on the
 * page (static text, lot details from the API, labels, buttons) and keeps translating
 * content React renders later. No translation files are needed and the source content
 * stays in English.
 *
 * The chosen language is stored in Google's own `googtrans` cookie, so it persists across
 * page loads and links. English is the default; choosing English removes the cookie.
 *
 * Nothing here talks to the León Gallery API, backend, auth or bidding code.
 */
export const LANGUAGES = [
  { code: "en", short: "EN", label: "English" },
  { code: "ja", short: "JA", label: "日本語" },
  { code: "zh-TW", short: "繁中", label: "繁體中文" },
  { code: "zh-CN", short: "简中", label: "简体中文" },
  { code: "ko", short: "KO", label: "한국어" },
  { code: "es", short: "ES", label: "Español" },
  { code: "de", short: "DE", label: "Deutsch" },
  { code: "fr", short: "FR", label: "Français" },
  { code: "it", short: "IT", label: "Italiano" },
];

const SOURCE = "en";
const SCRIPT_ID = "lg-google-translate";
const STYLE_ID = "lg-google-translate-style";
const HOLDER_ID = "google_translate_element";
const COOKIE = "googtrans";

/* ------------------------------------------------------------------ cookie */

function readCookie(name) {
  const match = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
  return match ? decodeURIComponent(match[1]) : "";
}

function cookieDomains() {
  const host = window.location.hostname;
  // host-only cookie + the registrable domain (so www. and the bare domain agree)
  const parts = host.split(".");
  const domains = [""];
  if (parts.length >= 2 && !/^\d+$/.test(parts[parts.length - 1])) {
    domains.push("." + parts.slice(-2).join("."));
    if (parts.length > 2) domains.push("." + host);
  }
  return domains;
}

function writeLangCookie(code) {
  const value = `/${SOURCE}/${code}`;
  cookieDomains().forEach((d) => {
    document.cookie = `${COOKIE}=${value}; path=/${d ? "; domain=" + d : ""}; max-age=31536000; SameSite=Lax`;
  });
}

function clearLangCookie() {
  cookieDomains().forEach((d) => {
    document.cookie = `${COOKIE}=; path=/${d ? "; domain=" + d : ""}; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
  });
}

/** Current language code ("en" when nothing is set). */
export function getCurrentLanguage() {
  if (typeof document === "undefined") return SOURCE;
  const value = readCookie(COOKIE); // e.g. "/en/ja"
  const code = value.split("/")[2];
  return LANGUAGES.some((l) => l.code === code) ? code : SOURCE;
}

/* --------------------------------------------- keep React and the translator in sync */

/*
 * The translator replaces text nodes with its own <font> elements. When React later removes
 * or inserts nodes around them it can throw "removeChild / insertBefore: node is not a child".
 * This guard (the widely used fix for React + page translators) ignores those mismatches
 * instead of crashing. It is only installed while a translation is active, so the English
 * site runs exactly as before.
 */
function installDomGuard() {
  if (typeof Node !== "function" || !Node.prototype || Node.prototype.__lgTranslateGuard) return;
  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function removeChild(child) {
    if (child.parentNode !== this) return child;
    return originalRemoveChild.apply(this, arguments);
  };
  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function insertBefore(newNode, referenceNode) {
    if (referenceNode && referenceNode.parentNode !== this) return newNode;
    return originalInsertBefore.apply(this, arguments);
  };
  Node.prototype.__lgTranslateGuard = true;
}

/* ------------------------------------------------ hide Google's own toolbar / tooltips */

function injectStyles() {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = `
    /* Google's top banner, its iframe and the hover tooltip: hidden, the site's selector is used instead */
    .goog-te-banner-frame, iframe.goog-te-banner-frame, .skiptranslate > iframe,
    iframe.VIpgJd-ZVi9od-ORHb-OEVmcd, .VIpgJd-ZVi9od-ORHb-OEVmcd,
    #goog-gt-tt, .goog-te-balloon-frame, .VIpgJd-ZVi9od-aZ2wEe-wOHMyf { display: none !important; visibility: hidden !important; }
    body { top: 0 !important; } /* Google pushes the page down for its banner */
    /* No highlight on translated text */
    .goog-text-highlight { background-color: transparent !important; box-shadow: none !important; }
    #${HOLDER_ID} { position: absolute !important; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; }
  `;
  document.head.appendChild(style);
}

/* -------------------------------------------------------------------- the script */

let loading = null;

function loadTranslator() {
  if (loading) return loading;
  loading = new Promise((resolve) => {
    injectStyles();
    installDomGuard();

    if (!document.getElementById(HOLDER_ID)) {
      const holder = document.createElement("div");
      holder.id = HOLDER_ID;
      holder.setAttribute("aria-hidden", "true");
      document.body.appendChild(holder);
    }

    window.googleTranslateElementInit = function googleTranslateElementInit() {
      new window.google.translate.TranslateElement(
        {
          pageLanguage: SOURCE,
          includedLanguages: LANGUAGES.map((l) => l.code).join(","),
          autoDisplay: false,
        },
        HOLDER_ID
      );
      resolve();
    };

    if (!document.getElementById(SCRIPT_ID)) {
      const script = document.createElement("script");
      script.id = SCRIPT_ID;
      script.src = "https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
      script.async = true;
      script.onerror = () => resolve(); // offline / blocked: the site simply stays in English
      document.body.appendChild(script);
    }
  });
  return loading;
}

function waitForCombo(timeoutMs = 6000) {
  return new Promise((resolve) => {
    const started = Date.now();
    (function check() {
      const combo = document.querySelector("select.goog-te-combo");
      if (combo && combo.options.length > 1) return resolve(combo);
      if (Date.now() - started > timeoutMs) return resolve(null);
      setTimeout(check, 100);
    })();
  });
}

/**
 * Call once on page load. If a language other than English was chosen earlier, the
 * translator is loaded and translates the page; for English nothing is loaded at all.
 */
export function initAutoTranslate() {
  if (typeof window === "undefined") return;
  if (getCurrentLanguage() !== SOURCE) loadTranslator();
}

/** Switch the whole site to `code`. English restores the original page. */
export async function setLanguage(code) {
  if (code === getCurrentLanguage()) return;

  if (code === SOURCE) {
    // Restoring the original text is only fully reliable with a fresh page.
    clearLangCookie();
    window.location.reload();
    return;
  }

  writeLangCookie(code);
  await loadTranslator();
  const combo = await waitForCombo();
  if (combo) {
    combo.value = code;
    combo.dispatchEvent(new Event("change"));
  } else {
    // Translator could not be driven in place — reload, the cookie applies the language.
    window.location.reload();
  }
}
